import * as FC from "@fluentui/react-components";
import { useCallback, useEffect, useState } from "react";
import { toDateTimeString } from "../../date_utils";
import {
    EQUIPMENT_DEFAULT_PAGE_SIZE,
    equipmentErrorMessage,
    equipmentErrorTrace,
    listEquipmentUsageHistory,
    usageRoleLabel,
} from "../../services/equipment_service";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "13px",
    },
    cell: {
        textAlign: "left",
        ...FC.shorthands.padding("6px"),
        borderBottom: `1px solid ${FC.tokens.colorNeutralStroke2}`,
    },
    hint: {
        color: FC.tokens.colorNeutralForeground3,
    },
    footer: {
        display: "flex",
        alignItems: "center",
        ...FC.shorthands.gap("8px"),
    },
});

/**
 * Where this unit has been (design 8.4's usage tab).
 *
 * Server-side paged like every other list in the module, so the count under the table is the
 * server's and not a slice of it.
 */
export const EquipmentUsagePanel = ({ equipmentId }) => {
    const styles = useStyles();

    const [page, setPage] = useState(1);
    const [result, setResult] = useState({ items: [], totalCount: 0, totalPages: 0 });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!equipmentId) {
            return;
        }
        setLoading(true);
        try {
            const loaded = await listEquipmentUsageHistory(equipmentId, {
                page,
                pageSize: EQUIPMENT_DEFAULT_PAGE_SIZE,
            });
            setResult(loaded);
            setError("");
        } catch (loadError) {
            console.error("Failed to load equipment usage history:", loadError);
            // A failed read must not render as "never used": that is a claim about the business,
            // and the operator would act on it.
            setResult({ items: [], totalCount: 0, totalPages: 0 });
            const suffix = equipmentErrorTrace(loadError);
            setError(`${equipmentErrorMessage(loadError)}${suffix ? `（${suffix}）` : ""}`);
        } finally {
            setLoading(false);
        }
    }, [equipmentId, page]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className={styles.root}>
            {error && (
                <FC.MessageBar intent="error">
                    <FC.MessageBarBody>{error}</FC.MessageBarBody>
                </FC.MessageBar>
            )}

            <table className={styles.table}>
                <thead>
                    <tr>
                        {["会话编号", "会话名称", "巡检日期", "用途", "开始", "结束"].map((label) => (
                            <th className={styles.cell} key={label}>
                                {label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {result.items.map((row) => (
                        <tr key={row.id}>
                            <td className={styles.cell}>{row.sessionSeq}</td>
                            <td className={styles.cell}>{row.sessionName}</td>
                            <td className={styles.cell}>
                                {row.sessionInspectionDate
                                    ? String(row.sessionInspectionDate).slice(0, 10)
                                    : "-"}
                            </td>
                            <td className={styles.cell}>{usageRoleLabel(row.usageRole)}</td>
                            <td className={styles.cell}>{toDateTimeString(row.usedFromUtc)}</td>
                            <td className={styles.cell}>
                                {row.isActive ? (
                                    <FC.Badge appearance="tint" color="success">
                                        占用中
                                    </FC.Badge>
                                ) : (
                                    toDateTimeString(row.usedToUtc)
                                )}
                            </td>
                        </tr>
                    ))}
                    {!error && result.items.length === 0 && (
                        <tr>
                            <td className={`${styles.cell} ${styles.hint}`} colSpan={6}>
                                {loading ? "加载中..." : "暂无巡检使用记录"}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            <div className={styles.footer}>
                <FC.Caption1 className={styles.hint}>共 {result.totalCount} 条</FC.Caption1>
                <div style={{ flex: 1 }} />
                <FC.Button
                    size="small"
                    disabled={loading || page <= 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                    上一页
                </FC.Button>
                <FC.Caption1>
                    {result.page || page} / {Math.max(result.totalPages || 0, 1)}
                </FC.Caption1>
                <FC.Button
                    size="small"
                    disabled={loading || page >= (result.totalPages || 1)}
                    onClick={() => setPage((current) => current + 1)}
                >
                    下一页
                </FC.Button>
            </div>
        </div>
    );
};
